/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/pricelist-prefill.ts
 * FILE_ID: QB-AGGRID-PRICELIST-PREFILL-001
 * PURPOSE: Pure I→O helpers mapping a chosen PriceBookItem to the
 *          prefill payload the AG-Grid section uses to seed a row's
 *          description / unit / rate / chargeout fields. Two flavours:
 *          one for ADDING a new (pad) row via onAddItem, one for
 *          PATCHING an existing focused row via onUpdateItem.
 * WHY: SAM's quote builder lets estimators pick a material from a
 *      pricelist and the row "fills in" — description, unit, rate
 *      all auto-populated. Quotech had to type each value manually,
 *      which is slow and error-prone (typos in material names, unit
 *      mismatches, stale rates). This module is the bridge between
 *      the pricebook data model and the QuoteLineItem fields the row
 *      cares about.
 * FLOW:
 *   ┌────────────────┐   ┌─────────────────────────┐   ┌────────────────┐
 *   │ PriceBookItem  │ → │ buildPrefill* (pure)    │ → │ row patch /    │
 *   │ from picker    │   │                         │   │ create payload │
 *   └────────────────┘   └─────────────────────────┘   └────────────────┘
 * DEPENDENCIES: none (pure).
 * RELATED:
 *   - usePricelistPicker.ts — consumes both builders
 *   - PricelistPickerModal.tsx — modal that picks the PriceBookItem
 *   - pricing-api/types.ts — PriceBookItem shape
 */

import type { PriceBookItem } from '@/lib/api/pricing-api/types'
import type { QuoteLineItem } from '@/lib/quoteApi'
import type { PadRowSeed } from './pad-row-helpers'

/**
 * Resolve the description text to seed into a row. Prefers
 * `item.description` over `item.item_name` because estimators tend
 * to write longer specs in `description` (e.g. "65mm Zincalume + R2
 * insulation, fixed to purlins") while `item_name` is the short code
 * label ("ZINC-65"). Falls back to item_name when description is
 * empty, item_code when both are empty.
 */
export function resolvePricelistDescription(item: PriceBookItem): string {
  if (item.description && item.description.trim().length > 0) {
    return item.description
  }
  if (item.item_name && item.item_name.trim().length > 0) {
    return item.item_name
  }
  return item.item_code ?? ''
}

/**
 * Resolve the sell price (per unit) to seed into adjusted_rate.
 * Order of preference:
 *   1. computed_sell_price (server-computed final, includes markup)
 *   2. fixed_sell_price (for items with pricing_method='fixed')
 *   3. base_cost (raw supplier price — last resort, estimator should
 *      review the markup themselves)
 *
 * Returns 0 when nothing usable is available — preferable to null
 * because the grid renders null as a blank cell and the estimator
 * might miss that they need to set a rate.
 */
export function resolvePricelistRate(item: PriceBookItem): number {
  const candidates: Array<string | number | null | undefined> = [
    item.computed_sell_price,
    item.fixed_sell_price,
    item.base_cost,
  ]
  for (const c of candidates) {
    if (c === null || c === undefined) continue
    const n = typeof c === 'number' ? c : Number(c)
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

/**
 * Resolve the productivity (unit per man per day) the pricelist row
 * carries, normalised to a finite positive number. Returns `null` when
 * the row has no productivity set (waterproofing pricelists carry it;
 * steel + generic pricelists usually don't). Callers use the `null`
 * signal to decide whether to OVERWRITE the row's current PMPD or
 * leave the estimator's manual value alone.
 */
export function resolvePricelistProductivity(
  item: PriceBookItem,
): number | null {
  const raw = item.productivity_pmpd
  if (raw === null || raw === undefined) return null
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Build the PATCH payload sent through onUpdateItem when the user
 * picks a pricelist item for an EXISTING row. Description / unit /
 * sell-rate always update. `productivity_pmpd` only appears when the
 * picked pricelist row carries one — we never overwrite an existing
 * manual productivity with a null from a row that has no labour data.
 */
export function buildPricelistUpdatePatch(
  item: PriceBookItem,
): Partial<QuoteLineItem> {
  const patch: Partial<QuoteLineItem> = {
    description: resolvePricelistDescription(item),
    unit: item.unit ?? 'm2',
    adjusted_rate: resolvePricelistRate(item),
  }
  const pmpd = resolvePricelistProductivity(item)
  if (pmpd !== null) {
    patch.productivity_pmpd = pmpd
  }
  return patch
}

/**
 * Build the CREATE payload sent through onAddItem when the user picks
 * a pricelist item for a TRAILING PAD row (no real row yet). Mirrors
 * the pad-promote payload shape — sort_order + productivity defaults
 * from the pad seed — and layers in the pricelist values on top.
 * When the pricelist row has its own productivity_pmpd, it wins over
 * the pad-seed default; otherwise the seed default carries through.
 */
export function buildPricelistCreatePayload(
  item: PriceBookItem,
  seed: PadRowSeed,
): Record<string, unknown> {
  const pricelistPmpd = resolvePricelistProductivity(item)
  return {
    description: resolvePricelistDescription(item),
    quantity: 0,
    unit: item.unit ?? seed.defaultUnit,
    productivity_pmpd: pricelistPmpd ?? seed.defaultProductivityPmpd,
    adjusted_rate: resolvePricelistRate(item),
    sort_order: seed.sortOrder,
  }
}
